import { StyleSheet } from 'aphrodite';
import {
	antiFlashWhite,
	borderColor,
	brandPrimary,
	charmGray,
	darkGrayFontColor,
	destructive,
	inputBorderColor,
	titles,
	white,
} from '../../styles/colors';
import { shadowMd, shadowSm } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

export const s = StyleSheet.create({
	input: {
		boxSizing: 'border-box',
		display: 'flex',
		width: '100%',
		background: white,
		border: `1px solid ${inputBorderColor}`,
		boxShadow: shadowSm,
		transition: 'color, box-shadow',
		color: titles,
		minHeight: 36,
		padding: '4px 12px',
		fontSize: '0.8125rem',
		lineHeight: 1.43,
		borderRadius: THEME.common.borderRadius,
		outline: 'none',
		':focus-visible': {
			borderColor: brandPrimary,
			outline: 'none',
		},
		':disabled': {
			cursor: 'not-allowed',
			opacity: 0.6,
		},
		'::placeholder': {
			color: `${darkGrayFontColor}CC`,
		},
		':is([readonly])': {
			background: `${antiFlashWhite}CC`,
			cursor: 'not-allowed',
		},
		':is([aria-invalid="true"])': {
			borderColor: `${destructive}99`,
			boxShadow: `${destructive}1A 0px 0px 0px 3px`,
		},
		':has([data-slot="combobox-input"]:focus-visible)': {
			borderColor: brandPrimary,
			outline: 'none',
		},
		':has(*) [data-slot="combobox-input"]': {
			flexGrow: 1,
		},
		':is([data-slot="combobox-input"])~[data-slot="combobox-icon"]': {
			right: 8,
		},
		':is([data-slot="combobox-input"])~[data-slot="combobox-clear"]': {
			right: 24,
		},
	},
	control: {
		position: 'relative',
	},
	status: {
		padding: '6px 8px',
		color: darkGrayFontColor,
		fontSize: '0.875rem',
	},
	positioner: {
		zIndex: 50,
		outline: 'none',
	},
	popup: {
		paddingTop: 4,
		paddingBottom: 4,
		width: 'var(--anchor-width)',
		maxHeight: 'min(var(--available-height), 368px)',
		maxWidth: 'var(--available-width)',
		overflowY: 'auto',
		scrollPaddingTop: 8,
		scrollPaddingBottom: 8,
		overscrollBehavior: 'contain',
		background: white,
		borderRadius: 8,
		border: `1px solid ${borderColor}`,
		color: titles,
		boxShadow: shadowMd,
		transformOrigin: 'var(--transform-origin)',
		transition: 'transform, scale, opacity',
		':is([data-ending-style])': {
			scale: 0.9,
			opacity: 0,
		},
		':is([data-starting-style])': {
			scale: 0.9,
			opacity: 0,
		},
	},
	list: {
		gap: 2,
		display: 'flex',
		flexDirection: 'column',
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	item: {
		color: titles,
		position: 'relative',
		display: 'flex',
		cursor: 'default',
		userSelect: 'none',
		alignItems: 'center',
		gap: 8,
		borderRadius: 8,
		paddingLeft: 28,
		paddingRight: 8,
		paddingTop: 6,
		paddingBottom: 6,
		fontSize: '0.875rem',
		outline: 'none',
		transitionProperty: 'color, background-color, border-color',
		transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		transitionDuration: '150ms',
		':is([data-disabled])': {
			pointerEvents: 'none',
			opacity: 0.5,
		},
		':has(*) svg': {
			pointerEvents: 'none',
			opacity: 0.6,
			flexShrink: 0,
		},
		':is([data-highlighted])': {
			position: 'relative',
			zIndex: 0,
			color: titles,
		},
		':is([data-selected])': {
			position: 'relative',
			zIndex: 0,
			color: titles,
		},
		':is([data-highlighted]):before': {
			content: '""',
			position: 'absolute',
			left: 4,
			right: 4,
			top: 0,
			bottom: 0,
			zIndex: -1,
			borderRadius: 4,
			background: `#f4f5f7`,
		},
		':is([data-selected]):before': {
			content: '""',
			position: 'absolute',
			left: 4,
			right: 4,
			top: 0,
			bottom: 0,
			zIndex: -1,
			borderRadius: 4,
			background: `rgba(0, 102, 255, 0.1)`,
		},
	},
	itemIndicator: {
		position: 'absolute',
		display: 'flex',
		top: '50%',
		left: 10,
		transform: 'translateY(-50%)',
		alignItems: 'center',
		justifyContent: 'center',
		':has(*) svg': {
			height: 12,
			width: 12,
			color: brandPrimary,
		},
	},
	groupLabel: {
		padding: '6px 8px',
		fontSize: '0.75rem',
		color: darkGrayFontColor,
		fontWeight: 'bold',
	},
	empty: {
		padding: '6px 8px',
		fontSize: '0.875rem',
		color: darkGrayFontColor,
		':empty': {
			margin: 0,
			padding: 0,
		},
	},
	clear: {
		position: 'absolute',
		cursor: 'pointer',
		top: '50%',
		right: 24,
		transform: 'translateY(-50%)',
		borderRadius: 4,
		opacity: 0.7,
		transition: 'opacity',
		background: 'transparent',
		border: 'none',
		padding: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: titles,
		':hover': {
			opacity: 1,
		},
		':focus': {
			outline: 'none',
			boxShadow: `${brandPrimary} 0px 0px 0px 2px`,
		},
		':disabled': {
			pointerEvents: 'none',
		},
		':is([data-disabled])': {
			pointerEvents: 'none',
		},
		':has(*) svg': {
			display: 'block',
			opacity: 1,
		},
	},
	icon: {
		position: 'absolute',
		cursor: 'pointer',
		top: '50%',
		right: 8,
		transform: 'translateY(-50%)',
		borderRadius: 4,
		opacity: 0.7,
		transition: 'opacity',
		color: titles,
		':focus': {
			outline: 'none',
			boxShadow: `${brandPrimary} 0px 0px 0px 2px`,
		},
		':disabled': {
			pointerEvents: 'none',
		},
		':is([data-disabled])': {
			pointerEvents: 'none',
		},
		':has(*) svg': {
			display: 'block',
			opacity: 1,
		},
	},
	trigger: {
		position: 'relative',
	},
	chips: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		paddingLeft: 4,
		paddingRight: 4,
		gap: 4,
		':has(*) [data-slot="combobox-input"]': {
			paddingTop: 0,
			paddingBottom: 0,
			paddingLeft: 6,
			paddingRight: 6,
			minHeight: 0,
			flex: 1,
			border: 0,
			boxShadow: 'none',
			borderRadius: 0,
			outline: 'none',
		},
		':has([data-slot="combobox-chip"]) [data-slot="combobox-input"]': {
			paddingLeft: 0,
			paddingRight: 0,
		},
	},
	chip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		borderRadius: 6,
		background: antiFlashWhite,
		padding: '4px 8px',
		fontSize: '0.75rem',
		fontWeight: 'bold',
		color: titles,
	},
	chipRemove: {
		cursor: 'pointer',
		marginLeft: 4,
		borderRadius: 4,
		padding: 4,
		color: titles,
		':hover': {
			background: `${darkGrayFontColor}33`,
		},
		':has(*) svg': {
			display: 'block',
			opacity: 0.6,
		},
		':has(*):hover svg': {
			opacity: 1,
		},
	},
	separator: {
		marginTop: 6,
		marginBottom: 6,
		height: 1,
		background: antiFlashWhite,
	},
	checkBoxItemCheckWrapper: {
		position: 'absolute',
		top: '50%',
		left: 10,
		transform: 'translateY(-50%)',
		border: `1px solid ${charmGray}`,
		width: 12,
		height: 12,
		flexShrink: 0,
		borderRadius: 4,
		display: `flex`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	checkBoxItemIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioItemCheckWrapper: {
		position: 'absolute',
		top: '50%',
		left: 10,
		transform: 'translateY(-50%)',
		border: `1px solid ${charmGray}`,
		width: 12,
		height: 12,
		flexShrink: 0,
		borderRadius: '50%',
		display: `flex`,
		alignItems: 'center',
		justifyContent: 'center',
	},
	radioItemCheckIndicator: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});
